import {APP_INITIALIZER, ApplicationConfig, importProvidersFrom, provideZoneChangeDetection} from '@angular/core';
import {TitleStrategy} from '@angular/router';
import {TranslateTitleStrategy} from './services/translate-title-strategy';
import {provideRouter} from '@angular/router';
import {
  NbDatepickerModule,
  NbDialogModule,
  NbMenuModule,
  NbSidebarModule,
  NbThemeModule,
  NbTimepickerModule,
  NbToastrModule
} from '@nebular/theme';
import {TranslateLoader, TranslateModule, TranslateService} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {HttpClient} from '@angular/common/http';

import {routes} from './app.routes';
import {provideHttpClient, withInterceptors} from '@angular/common/http';
import {authInterceptor} from './services/interceptors/auth.interceptor';
import {errorInterceptor} from './services/interceptors/error.interceptor';
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async';
import {Utils} from './shared/utils';
import {NgxPermissionsModule} from 'ngx-permissions';
import {fr} from 'date-fns/locale/fr';
import {enUS} from 'date-fns/locale/en-US';
import {enGB} from 'date-fns/locale/en-GB';
import {Locale} from "date-fns";
import {jwtInterceptor} from "./services/interceptors/jwt.interceptor";

interface DateFormatConfig {
  parseFormat: string;
  format: string;
  parseTimeFormat: string;
  formatTime: string;
  formatDateTime: string;
  locale: Locale;
}

const DATE_FORMATS_BY_LANGUAGE: Record<string, DateFormatConfig> = {
  // Français (France, Belgique, Suisse, Canada, Sénégal, etc.)
  'fr': {
    parseFormat: 'dd/MM/yyyy',
    format: 'dd/MM/yyyy',
    parseTimeFormat: 'HH:mm:ss',
    formatTime: 'HH:mm',
    formatDateTime: 'dd/MM/yyyy HH:mm',
    locale: fr,
  },
  'fr-FR': {
    parseFormat: 'dd/MM/yyyy',
    format: 'dd/MM/yyyy',
    parseTimeFormat: 'HH:mm:ss',
    formatTime: 'HH:mm',
    formatDateTime: 'dd/MM/yyyy HH:mm',
    locale: fr,
  },
  'fr-BE': {
    parseFormat: 'dd/MM/yyyy',
    format: 'dd/MM/yyyy',
    parseTimeFormat: 'HH:mm:ss',
    formatTime: 'HH:mm',
    formatDateTime: 'dd/MM/yyyy HH:mm',
    locale: fr,
  },
  'fr-CA': {
    parseFormat: 'yyyy-MM-dd',
    format: 'yyyy-MM-dd',
    parseTimeFormat: 'HH:mm:ss',
    formatTime: 'HH:mm',
    formatDateTime: 'yyyy-MM-dd HH:mm',
    locale: fr,
  },
  'fr-SN': {
    parseFormat: 'dd/MM/yyyy',
    format: 'dd/MM/yyyy',
    parseTimeFormat: 'HH:mm:ss',
    formatTime: 'HH:mm',
    formatDateTime: 'dd/MM/yyyy HH:mm',
    locale: fr,
  },
  // Anglais US
  'en': {
    parseFormat: 'MM/dd/yyyy',
    format: 'MM/dd/yyyy',
    parseTimeFormat: 'hh:mm:ss a',
    formatTime: 'hh:mm a',
    formatDateTime: 'MM/dd/yyyy hh:mm a',
    locale: enUS,
  },
  'en-US': {
    parseFormat: 'MM/dd/yyyy',
    format: 'MM/dd/yyyy',
    parseTimeFormat: 'hh:mm:ss a',
    formatTime: 'hh:mm a',
    formatDateTime: 'MM/dd/yyyy hh:mm a',
    locale: enUS,
  },
  // Anglais UK
  'en-GB': {
    parseFormat: 'dd/MM/yyyy',
    format: 'dd/MM/yyyy',
    parseTimeFormat: 'HH:mm:ss',
    formatTime: 'HH:mm',
    formatDateTime: 'dd/MM/yyyy HH:mm',
    locale: enGB,
  },
};

function getDateFormatConfig(): DateFormatConfig {
  const browserLang = getBrowserLocale();

  // Essayer d'abord la langue complète (ex: fr-FR)
  if (DATE_FORMATS_BY_LANGUAGE[browserLang]) {
    return DATE_FORMATS_BY_LANGUAGE[browserLang];
  }

  // Essayer la langue courte (ex: fr)
  const shortLang = browserLang.split('-')[0];
  if (DATE_FORMATS_BY_LANGUAGE[shortLang]) {
    // console.log('✅ Configuration trouvée pour:', shortLang);
    return DATE_FORMATS_BY_LANGUAGE[shortLang];
  }

  // Par défaut: format ISO international
  // console.log('⚠️ Langue non supportée, utilisation du format par défaut (ISO)');
  return {
    parseFormat: 'yyyy-MM-dd',
    format: 'yyyy-MM-dd',
    parseTimeFormat: 'HH:mm:ss',
    formatTime: 'HH:mm',
    formatDateTime: 'yyyy-MM-dd HH:mm',
    locale: enUS,
  };
}

// Fonction pour obtenir le LOCALE_ID
function getBrowserLocale(): string {
  return navigator.language || 'en-US';
}

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: Utils, useClass: Utils },
    { provide: TitleStrategy, useClass: TranslateTitleStrategy },
    {
      provide: APP_INITIALIZER,
      useFactory: (translate: TranslateService) => () => {
        const locale = localStorage.getItem('locale') || 'en';
        translate.setDefaultLang('en');
        return translate.use(locale).toPromise();
      },
      deps: [TranslateService],
      multi: true,
    },
    provideHttpClient(
      withInterceptors([jwtInterceptor, authInterceptor, errorInterceptor])
    ),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimationsAsync(),
    importProvidersFrom(NbThemeModule.forRoot({ name: 'ig' })),
    importProvidersFrom(NbSidebarModule.forRoot()),
    importProvidersFrom(NbMenuModule.forRoot()),
    importProvidersFrom(NbToastrModule.forRoot({})),
    importProvidersFrom(NgxPermissionsModule.forRoot({})),
    importProvidersFrom(NbDialogModule.forRoot({})),
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: 'en',
        loader: {
          provide: TranslateLoader,
          useFactory: (http: HttpClient) => new TranslateHttpLoader(http, '/assets/i18n/', '.json'),
          deps: [HttpClient],
        },
      })
    ),
    importProvidersFrom(
      NbDatepickerModule.forRoot(),
      NbTimepickerModule.forRoot(),
      // NbDateFnsDateModule.forRoot({  // IMPORTANT: forRoot() avec configuration
      //   parseOptions: { useAdditionalWeekYearTokens: true, useAdditionalDayOfYearTokens: true },
      //   formatOptions: { useAdditionalWeekYearTokens: true, useAdditionalDayOfYearTokens: true },
      //   // format: getDateFormatConfig().format,  // FORMAT OBLIGATOIRE
      //   // formatDateTime: getDateFormatConfig().formatDateTime,
      // })
    ),
  ]
};
